import React, { useEffect, useState } from 'react';
import { getStats, getExpectedSummary, getExpected, syncWarehouse, syncAll, matchExpected, getShipments, recountAll, getRecountStatus, cancelRecount, deleteExpected } from '../../api';
import { useNavigate } from 'react-router-dom';
import InsideImagesModal from '../../components/InsideImagesModal';

const WAREHOUSE_NAMES = {
  'Kho 1': 'Kho 4450 Van',
  'Kho 2': 'Kho 7320 Cal',
  'Kho 3': 'Kho 3838/135 Cal',
  'ARBOURRIDGE': 'ARBOURRIDGE (Cal)',
  'Kho 4': 'Kho 465 Toronto',
  'Kho Surrey': 'Kho Surrey',
};
const WAREHOUSE_CODES = ['Kho 1', 'Kho 2', 'Kho 3', 'Kho 4', 'Kho Surrey', 'ARBOURRIDGE'];

// Component for each expected code — shows the code + "🔗 Assign" and "×" (delete) actions
function ExpectedRow({ exp, onOpenMatch, onDelete }) {
  return (
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',marginBottom:8,
      background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.06)',borderRadius:8}}>
      <div style={{fontVariantNumeric:'tabular-nums',fontWeight:600,fontSize:13,fontWeight:700,color:'var(--color-info)',wordBreak:'break-all',marginRight:8}}>{exp.code}</div>
      <div style={{display:'flex',gap:6,flexShrink:0}}>
        <button onClick={()=>onOpenMatch(exp)}
          style={{background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.22)',borderRadius:5,padding:'4px 12px',fontSize:11,color:'var(--color-warning)',cursor:'pointer',whiteSpace:'nowrap'}}>
          🔗 Assign
        </button>
        <button onClick={()=>onDelete(exp)} title="Delete this stale code"
          style={{background:'transparent',border:'1px solid rgba(220,38,38,0.22)',borderRadius:5,padding:'4px 9px',fontSize:12,color:'#b91c1c',cursor:'pointer',lineHeight:1}}>
          ×
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState([]);
  const [modal, setModal] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllResult, setSyncAllResult] = useState(null);
  // Tier 1: customer list
  const [customerList, setCustomerList] = useState([]);
  // Tier 2: current customer + expected codes + warehouse shipments
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerExpected, setCustomerExpected] = useState([]);
  const [warehouseShipments, setWarehouseShipments] = useState([]); // all warehouse shipments
  // AI counting
  const [recountOpen, setRecountOpen] = useState(false);
  const [recountStatus, setRecountStatus] = useState(null);
  const [recountStarting, setRecountStarting] = useState(false);
  const [recountOnlyMissing, setRecountOnlyMissing] = useState(true);
  const navigate = useNavigate();

  // Poll status while modal is open
  useEffect(() => {
    if (!recountOpen) return;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await getRecountStatus();
        if (!stopped) setRecountStatus(s);
      } catch(e) { console.error(e); }
    };
    poll();
    const iv = setInterval(poll, 2000);
    return () => { stopped = true; clearInterval(iv); };
  }, [recountOpen]);

  const handleStartRecount = async () => {
    setRecountStarting(true);
    try {
      await recountAll({ only_missing: recountOnlyMissing });
      const s = await getRecountStatus();
      setRecountStatus(s);
    } catch(e) { alert(e.message); }
    finally { setRecountStarting(false); }
  };

  const handleCancelRecount = async () => {
    if (!window.confirm('Stop the AI count job?')) return;
    try { await cancelRecount(); }
    catch(e) { alert(e.message); }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    setSyncAllResult(null);
    try {
      const result = await syncAll();
      setSyncAllResult(result);
      getExpectedSummary().then(setSummary).catch(console.error);
    } catch(e) { console.error(e); }
    finally { setSyncingAll(false); }
  };

  const load = () => {
    getStats().then(setStats).catch(console.error);
    getExpectedSummary().then(setSummary).catch(console.error);
  };
  useEffect(() => { load(); }, []);

  const handleSyncWarehouse = async (warehouse) => {
    setSyncing(true); setSyncResult(null);
    try {
      const result = await syncWarehouse(warehouse);
      setSyncResult(result);
      const data = await getExpected({ warehouse, status: 'pending' });
      // Rebuild customer list
      await openWarehouse(warehouse);
      getExpectedSummary().then(setSummary).catch(console.error);
    } catch(e) { console.error(e); }
    finally { setSyncing(false); }
  };

  const openWarehouse = async (warehouse) => {
    setModal({ warehouse });
    setSelectedCustomer(null);
    setCustomerList([]);
    setCustomerExpected([]);
    setWarehouseShipments([]);
    setSyncResult(null);
    setModalLoading(true);
    try {
      // Fetch pending expected codes, group by customer
      const expectedData = await getExpected({ warehouse, status: 'pending' });
      const byCustomer = {};
      expectedData.forEach(e => {
        const key = e.customer_id || '__none__';
        if (!byCustomer[key]) byCustomer[key] = {
          customer_id: e.customer_id,
          customer_name: e.customer_name || 'Unassigned',
          customer_code: e.customer_code || '—',
          expected: []
        };
        byCustomer[key].expected.push(e);
      });
      const sorted = Object.values(byCustomer).sort((a,b) => a.customer_id ? -1 : 1);
      setCustomerList(sorted);

      // Fetch ALL shipments for the warehouse (including assigned ones — for admin fix)
      const shipData = await getShipments({ warehouse });
      setWarehouseShipments(shipData);
    } catch(e) { console.error(e); }
    finally { setModalLoading(false); }
  };

  const openCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomerExpected(cust.expected);
  };

  const [reloadCount, setReloadCount] = useState(0);
  // Match flow: search → confirm → success (copies UX from ShipmentsPending)
  const [matchModal, setMatchModal] = useState(null); // { exp } — picking shipment for which expected
  const [matchSearch, setMatchSearch] = useState('');
  const [imageModal, setImageModal] = useState(null); // url
  const [insideModal, setInsideModal] = useState(null); // {id, tracking}
  const [matchConfirm, setMatchConfirm] = useState(null); // {expectedId, expectedCode, shipmentId, shipmentTracking, shipmentReceiver, shipmentOrder, shipmentExpectedMatched}
  const [matchSuccess, setMatchSuccess] = useState(null); // {code, tracking}
  const [matchLoading, setMatchLoading] = useState(false);

  const openMatch = (exp) => {
    setMatchModal({ exp });
    setMatchSearch('');
    setMatchConfirm(null);
    setMatchSuccess(null);
  };

  const handleDeleteExpected = async (exp) => {
    if (!window.confirm(`Delete expected code "${exp.code}"?\nThis clears the pending registration — the customer will need to re-register if the package still arrives.`)) return;
    try {
      await deleteExpected(exp.id);
      // Reload tier 2 in place + refresh tier 1 counts
      setCustomerExpected(list => list.filter(e => e.id !== exp.id));
      if (selectedCustomer) {
        setSelectedCustomer(cust => ({ ...cust, expected: cust.expected.filter(e => e.id !== exp.id) }));
      }
      setCustomerList(list => list
        .map(c => c.customer_id === (selectedCustomer && selectedCustomer.customer_id)
          ? { ...c, expected: c.expected.filter(e => e.id !== exp.id) }
          : c)
        .filter(c => c.expected.length > 0));
      getExpectedSummary().then(setSummary).catch(console.error);
    } catch (e) {
      alert(e.message || 'Failed to delete');
    }
  };

  const handleDeleteCustomerExpected = async (cust) => {
    const n = cust.expected.length;
    if (!window.confirm(`Delete ALL ${n} pending code${n>1?'s':''} for ${cust.customer_code} — ${cust.customer_name}?\nCustomers will need to re-register if any of these packages still arrive.`)) return;
    try {
      await Promise.all(cust.expected.map(e => deleteExpected(e.id)));
      // Remove customer from tier 1
      setCustomerList(list => list.filter(c => c.customer_id !== cust.customer_id));
      // If viewing tier 2 for this customer, close back to tier 1
      if (selectedCustomer && selectedCustomer.customer_id === cust.customer_id) {
        setSelectedCustomer(null);
        setCustomerExpected([]);
      }
      getExpectedSummary().then(setSummary).catch(console.error);
    } catch (e) {
      alert(e.message || 'Failed to delete some codes');
      // Reload from server to reconcile partial failure
      if (modal?.warehouse) openWarehouse(modal.warehouse);
    }
  };

  const pickShipment = (exp, shipment) => {
    setMatchConfirm({
      expectedId: exp.id,
      expectedCode: exp.code,
      shipmentId: shipment.id,
      shipmentTracking: shipment.tracking_code,
      shipmentReceiver: shipment.receiver_name,
      shipmentOrder: shipment.customer_order,
      shipmentExpectedMatched: !!shipment.expected_matched,
      shipmentFlightName: shipment.flight_name,
      shipmentFlightStatus: shipment.flight_status,
    });
  };

  const handleConfirmMatch = async () => {
    if (!matchConfirm) return;
    setMatchLoading(true);
    try {
      await matchExpected(matchConfirm.expectedId, matchConfirm.shipmentId);
      setMatchSuccess({ code: matchConfirm.expectedCode, tracking: matchConfirm.shipmentTracking });
      setMatchConfirm(null);
      setReloadCount(c => c + 1);
      await openWarehouse(modal.warehouse);
      getExpectedSummary().then(setSummary).catch(console.error);
    } catch(e) { alert(e.message); }
    finally { setMatchLoading(false); }
  };

  const handleCloseSuccess = () => {
    setMatchSuccess(null);
    setMatchModal(null); // back to tier 2
  };

  const closeModal = () => {
    setModal(null);
    setSyncResult(null);
    setSelectedCustomer(null);
    setMatchModal(null);
    setMatchSearch('');
    setMatchConfirm(null);
    setMatchSuccess(null);
  };

  return (
    <div>
      <div style={s.topbar}>
        <div>
          <div className="page-eyebrow">OVERVIEW</div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Track pending arrivals and quickly update status</p>
        </div>
        <div style={{display:'flex',gap:8,flexShrink:0}}>
          <button className="btn btn-secondary" onClick={()=>setRecountOpen(true)}>
            <span className="material-symbols-outlined">smart_toy</span>
            AI Count
          </button>
          <button className="btn btn-primary" onClick={handleSyncAll} disabled={syncingAll}>
            <span className={`material-symbols-outlined ${syncingAll?'spin':''}`}>{syncingAll?'progress_activity':'sync'}</span>
            {syncingAll?'Syncing...':'Update status'}
          </button>
        </div>
      </div>

      {syncAllResult && (
        <div style={{padding:'10px 14px',marginBottom:20,background:'rgba(22,163,74,0.08)',border:'1px solid rgba(22,163,74,0.22)',borderRadius:'var(--radius-md)',fontSize:13,color:'var(--color-success)',display:'flex',alignItems:'center',gap:8}}>
          <span className="material-symbols-outlined" style={{fontSize:18,color:'var(--color-success)'}}>check_circle</span>
          Updated — {syncAllResult.updated}/{syncAllResult.total} codes matched
        </div>
      )}

      <div style={s.alertGrid}>
        <div className="card card-interactive" style={{...s.alertCard,borderLeft:'3px solid var(--color-warning)'}}
          onClick={()=>navigate('/shipments/pending?unmapped=true')}>
          <div style={s.alertLabel}>UNMAPPED</div>
          <div style={{...s.alertNum,color:'var(--color-warning)'}}>{stats?.unmapped??'—'}</div>
          <div style={s.alertSub}>Click to process →</div>
        </div>
        <div className="card card-interactive" style={{...s.alertCard,borderLeft:'3px solid var(--color-danger)'}}
          onClick={()=>navigate('/shipments/pending')}>
          <div style={s.alertLabel}>NO FLIGHT ASSIGNED</div>
          <div style={{...s.alertNum,color:'var(--color-danger)'}}>{stats?.no_flight??'—'}</div>
          <div style={s.alertSub}>Click to process →</div>
        </div>
      </div>

      <div className="section-label">
        <span className="section-num">01</span>
        <span>REGISTERED CODES NOT YET ARRIVED</span>
      </div>
      <div style={s.grid}>
        {WAREHOUSE_CODES.map(code=>{
          const pending=parseInt(summary.find(e=>e.warehouse===code)?.pending_count||0);
          const hasAny=pending>0;
          return(
            <div key={code} onClick={()=>hasAny&&openWarehouse(code)}
              className={hasAny?'card card-interactive':'card'}
              style={{...s.khoCard,
                ...(hasAny?{borderLeft:'3px solid var(--color-info)'}:{opacity:0.5,cursor:'default'})}}>
              <div style={s.khoName}>{WAREHOUSE_NAMES[code]||code}</div>
              <div style={s.khoInternal}>{code}</div>
              <div style={{...s.khoNum,color:hasAny?'var(--color-info)':'var(--text-muted)'}}>{pending}</div>
              <div style={s.khoLabel}>pending</div>
            </div>
          );
        })}
      </div>

      {/* AI Recount modal */}
      {recountOpen && (
        <div style={s.overlay} onClick={()=>setRecountOpen(false)}>
          <div style={{...s.modalBox,width:480}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14}}>
              <div>
                <div style={{fontSize:10,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:3}}>AI VISION</div>
                <div style={{fontSize:16,fontWeight:700,color:'var(--text-primary)'}}>🤖 Bulk AI Count</div>
              </div>
              <button onClick={()=>setRecountOpen(false)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:24}}>×</button>
            </div>

            {(!recountStatus || recountStatus.done) && (
              <>
                <div style={{padding:'10px 12px',background:'rgba(124,58,237,0.06)',border:'1px solid rgba(124,58,237,0.15)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',marginBottom:14,lineHeight:1.5}}>
                  Gemini will count items in the inside photos. Shipments without inside photos are skipped.
                </div>
                <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,cursor:'pointer'}}>
                  <input type="checkbox" checked={recountOnlyMissing} onChange={e=>setRecountOnlyMissing(e.target.checked)}
                    style={{accentColor:'var(--color-purple)'}} />
                  <span style={{fontSize:13,color:'var(--text-primary)'}}>Only count missing/errored shipments (skip already-counted and manually-edited)</span>
                </label>
                <button className="btn btn-primary" onClick={handleStartRecount} disabled={recountStarting}
                  style={{width:'100%',height:40,borderColor:'rgba(124,58,237,0.40)'}}>
                  <span className={`material-symbols-outlined ${recountStarting?'spin':''}`} style={{color:'var(--color-purple)'}}>{recountStarting?'progress_activity':'rocket_launch'}</span>
                  {recountStarting?'Starting...':'Start counting'}
                </button>
                {recountStatus?.done && recountStatus.total > 0 && (
                  <div style={{marginTop:14,padding:'10px 12px',background:'rgba(22,163,74,0.08)',border:'1px solid rgba(22,163,74,0.22)',borderRadius:8,fontSize:12,color:'var(--color-success)'}}>
                    ✅ Last job: {recountStatus.success}/{recountStatus.total} succeeded, {recountStatus.errors} errors
                    {recountStatus.cancelled && ' (cancelled)'}
                  </div>
                )}
              </>
            )}

            {recountStatus && !recountStatus.done && (
              <>
                <div style={{marginBottom:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                    <span style={{fontSize:12,color:'var(--text-secondary)'}}>{recountStatus.processed} / {recountStatus.total} shipments</span>
                    <span style={{fontSize:11,color:'var(--text-muted)'}}>
                      ✅ {recountStatus.success} · ⚠ {recountStatus.errors}
                    </span>
                  </div>
                  <div style={{height:6,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${recountStatus.total>0?(recountStatus.processed/recountStatus.total*100):0}%`,background:'var(--color-purple)',transition:'width 0.5s'}} />
                  </div>
                </div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:14}}>
                  Throttle {recountStatus.throttleMs}ms/req · ETA {Math.ceil((recountStatus.total - recountStatus.processed) * (recountStatus.throttleMs+5000) / 1000 / 60)} min
                </div>
                {recountStatus.lastError && (
                  <div style={{padding:'8px 10px',marginBottom:10,background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.20)',borderRadius:6,fontSize:11,color:'#b91c1c'}}>
                    Last error: {recountStatus.lastError}
                  </div>
                )}
                <button className="btn btn-danger" onClick={handleCancelRecount} style={{width:'100%'}}>
                  <span className="material-symbols-outlined">stop_circle</span>
                  Stop job
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalBox} onClick={e=>e.stopPropagation()}>
            {/* SUCCESS STATE — copies UX from ShipmentsPending */}
            {matchSuccess ? (
              <div style={{textAlign:'center',padding:'12px 0'}}>
                <div style={{fontSize:32,marginBottom:12}}>✅</div>
                <div style={{fontSize:15,fontWeight:700,color:'var(--color-success)',marginBottom:6}}>Assigned successfully!</div>
                <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:20,lineHeight:1.6}}>
                  Code <span style={{fontVariantNumeric:'tabular-nums',fontWeight:600,color:'var(--color-info)',fontWeight:700}}>{matchSuccess.code}</span>
                  {' '}was assigned to shipment <span style={{fontVariantNumeric:'tabular-nums',fontWeight:600,color:'var(--accent)',fontWeight:700}}>{matchSuccess.tracking}</span>
                </div>
                <button className="btn btn-primary" onClick={handleCloseSuccess}>
                  <span className="material-symbols-outlined">arrow_forward</span>
                  Continue
                </button>
              </div>

            /* CONFIRM STATE */
            ) : matchConfirm ? (
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',marginBottom:12}}>Confirm code assignment</div>
                <div style={{padding:'14px',background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:10,marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,marginBottom:6}}>REGISTRATION CODE</div>
                  <div style={{fontVariantNumeric:'tabular-nums',fontWeight:600,fontSize:15,fontWeight:700,color:'var(--color-info)',marginBottom:14,wordBreak:'break-all'}}>{matchConfirm.expectedCode}</div>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,marginBottom:6}}>SCANNED SHIPMENT</div>
                  <div style={{fontVariantNumeric:'tabular-nums',fontWeight:600,fontSize:14,fontWeight:700,color:'var(--accent)',marginBottom:4,wordBreak:'break-all'}}>{matchConfirm.shipmentTracking}</div>
                  <div style={{fontSize:11,color:'var(--text-secondary)'}}>
                    {matchConfirm.shipmentReceiver}{matchConfirm.shipmentOrder?` · ${matchConfirm.shipmentOrder}`:''}
                  </div>
                  {matchConfirm.shipmentExpectedMatched ? (
                    <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid rgba(217,119,6,0.20)',fontSize:12,color:'var(--color-warning)',lineHeight:1.6}}>
                      ⚠ This shipment is currently mapped to another registration code.<br/>
                      Confirming will switch to this code and unassign the old one.
                    </div>
                  ) : (
                    <div style={{marginTop:10,fontSize:12,color:'var(--text-secondary)'}}>
                      Assign this code to the scanned shipment?
                    </div>
                  )}
                  {matchConfirm.shipmentFlightName && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid rgba(124,58,237,0.18)',fontSize:11,color:'var(--color-purple)',lineHeight:1.6}}>
                      ✈ Shipment on flight <strong>{matchConfirm.shipmentFlightName}</strong> ({matchConfirm.shipmentFlightStatus==='flown'?'flown':'pending'}). Assigning the code does NOT unassign from the flight — to detach, use "Assigned Shipments".
                    </div>
                  )}
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-primary" onClick={handleConfirmMatch} disabled={matchLoading}
                    style={{flex:1,height:40,borderColor:matchConfirm.shipmentExpectedMatched?'rgba(217,119,6,0.35)':'var(--accent-strong)'}}>
                    <span className={`material-symbols-outlined ${matchLoading?'spin':''}`} style={{color:matchConfirm.shipmentExpectedMatched?'var(--color-warning)':'var(--accent)'}}>{matchLoading?'progress_activity':'check'}</span>
                    {matchLoading?'Assigning...':'Confirm assign'}
                  </button>
                  <button className="btn btn-secondary" onClick={()=>setMatchConfirm(null)} disabled={matchLoading} style={{flex:1,height:40}}>
                    Cancel
                  </button>
                </div>
              </div>

            /* SEARCH STATE — pick shipment for the expected just clicked "Assign" */
            ) : matchModal ? (
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                  <div>
                    <button onClick={()=>{ setMatchModal(null); setMatchSearch(''); }}
                      style={{background:'none',border:'none',color:'var(--color-info)',cursor:'pointer',fontSize:12,padding:0,marginBottom:4,display:'block'}}>
                      ← Code list
                    </button>
                    <div style={{fontSize:14,fontWeight:700,color:'var(--text-primary)'}}>Manual warehouse match</div>
                  </div>
                </div>
                <div style={{padding:'10px 12px',background:'rgba(37,99,235,0.08)',border:'1px solid rgba(37,99,235,0.20)',borderRadius:8,marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',letterSpacing:1,marginBottom:4}}>REGISTRATION CODE</div>
                  <div style={{fontVariantNumeric:'tabular-nums',fontWeight:600,fontSize:13,fontWeight:700,color:'var(--color-info)',wordBreak:'break-all'}}>{matchModal.exp.code}</div>
                </div>
                <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:10}}>Type tracking code or customer order to find a scanned shipment</div>
                <input
                  placeholder="Type a few digits to find a shipment..."
                  value={matchSearch}
                  onChange={e=>setMatchSearch(e.target.value)}
                  autoFocus
                  style={{width:'100%',background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.10)',borderRadius:8,padding:'8px 12px',fontSize:13,color:'var(--text-primary)',outline:'none',boxSizing:'border-box',marginBottom:12}}
                  onFocus={e=>e.target.style.borderColor='rgba(22,163,74,0.40)'}
                  onBlur={e=>e.target.style.borderColor='var(--border-strong)'}
                />
                <div style={{maxHeight:'45vh',overflowY:'auto'}}>
                  {!matchSearch.trim() ? (
                    <div style={{padding:'24px',textAlign:'center',color:'var(--text-muted)',fontSize:13}}>
                      Type to search codes
                    </div>
                  ) : (() => {
                    const q = matchSearch.trim().toLowerCase();
                    const filtered = warehouseShipments.filter(s =>
                      (s.tracking_code && s.tracking_code.toLowerCase().includes(q)) ||
                      (s.customer_order && s.customer_order.toLowerCase().includes(q))
                    );
                    if (filtered.length === 0) return (
                      <div style={{padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:13}}>
                        No matching shipments found
                      </div>
                    );
                    return filtered.map(sh => {
                      const isMatched = !!sh.expected_matched;
                      const onFlight = !!sh.flight_id;
                      const flown = sh.flight_status === 'flown';
                      return (
                        <div key={sh.id}
                          style={{display:'flex',gap:10,alignItems:'center',padding:'10px 12px',marginBottom:6,
                            background: isMatched?'rgba(217,119,6,0.06)':onFlight?'rgba(124,58,237,0.06)':'var(--bg-tertiary)',
                            border:`1px solid ${isMatched?'rgba(217,119,6,0.15)':onFlight?'rgba(124,58,237,0.18)':'var(--border)'}`,
                            borderRadius:8}}>
                          {sh.image_url ? (
                            <img src={sh.image_url} alt="label" onClick={()=>setImageModal(sh.image_url)}
                              style={{width:44,height:44,objectFit:'cover',borderRadius:6,border:'1px solid rgba(22,163,74,0.30)',flexShrink:0,cursor:'zoom-in'}} />
                          ) : (
                            <div style={{width:44,height:44,borderRadius:6,background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.06)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,opacity:0.2,flexShrink:0}}>📷</div>
                          )}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontVariantNumeric:'tabular-nums',fontWeight:600,fontSize:13,fontWeight:700,color:'var(--accent)',marginBottom:3}}>{sh.tracking_code}</div>
                            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4}}>{sh.receiver_name||'—'}{sh.customer_order?` · ${sh.customer_order}`:''}{sh.weight_kg?` · ${sh.weight_kg}kg`:''}</div>
                            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                              <span style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:20,
                                color: isMatched?'var(--color-warning)':'var(--text-muted)',
                                background: isMatched?'rgba(217,119,6,0.10)':'var(--border)',
                                border: `1px solid ${isMatched?'rgba(217,119,6,0.20)':'var(--border)'}`}}>
                                {isMatched?'⚠ Mapped to another code':'✅ Ready to assign'}
                              </span>
                              {onFlight && (
                                <span style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:20,
                                  color: flown?'var(--color-danger)':'var(--color-purple)',
                                  background: flown?'rgba(220,38,38,0.10)':'rgba(124,58,237,0.10)',
                                  border: `1px solid ${flown?'rgba(220,38,38,0.22)':'rgba(124,58,237,0.22)'}`}}>
                                  ✈ {sh.flight_name||'?'} · {flown?'Flown':'Pending'}
                                </span>
                              )}
                              <button onClick={()=>setInsideModal({id:sh.id,tracking:sh.tracking_code})}
                                style={{background:'rgba(217,119,6,0.10)',border:'1px solid rgba(217,119,6,0.20)',borderRadius:20,padding:'1px 8px',fontSize:10,fontWeight:600,color:'var(--color-warning)',cursor:'pointer'}}>
                                📦 Inside
                              </button>
                            </div>
                          </div>
                          <button onClick={()=>pickShipment(matchModal.exp, sh)}
                            style={{background:'rgba(37,99,235,0.15)',border:'1px solid rgba(37,99,235,0.25)',borderRadius:6,padding:'5px 14px',fontSize:12,fontWeight:600,color:'var(--color-info)',cursor:'pointer',flexShrink:0}}>
                            Assign
                          </button>
                        </div>
                      );
                    });
                  })()}
                </div>
                <button onClick={()=>{ setMatchModal(null); setMatchSearch(''); }}
                  style={{width:'100%',marginTop:12,background:'var(--border)',border:'1px solid rgba(15,23,42,0.08)',borderRadius:8,padding:'9px',fontSize:13,color:'var(--text-secondary)',cursor:'pointer'}}>
                  Close
                </button>
              </div>

            /* DEFAULT — Tier 1 / Tier 2 browse */
            ) : (
            <>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <div>
                  {selectedCustomer && (
                    <button onClick={()=>setSelectedCustomer(null)}
                      style={{background:'none',border:'none',color:'var(--color-info)',cursor:'pointer',fontSize:12,padding:0,marginBottom:4,display:'block'}}>
                      ←  Customer list
                    </button>
                  )}
                  <div style={{fontSize:10,color:'var(--text-muted)',fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,marginBottom:3}}>
                    {selectedCustomer ? selectedCustomer.customer_code + ' — ' + selectedCustomer.customer_name : 'PENDING ARRIVALS'}
                  </div>
                  <div style={{fontSize:16,fontWeight:700,color:'var(--text-primary)'}}>{WAREHOUSE_NAMES[modal.warehouse]||modal.warehouse}</div>
                </div>
                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                  {!selectedCustomer && (
                    <button className="btn btn-secondary btn-sm" onClick={()=>handleSyncWarehouse(modal.warehouse)} disabled={syncing||modalLoading}>
                      <span className={`material-symbols-outlined ${syncing?'spin':''}`} style={{fontSize:14}}>{syncing?'progress_activity':'sync'}</span>
                      {syncing?'Syncing...':'Refresh'}
                    </button>
                  )}
                  <button onClick={closeModal}
                    style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:24}}>×</button>
                </div>
              </div>

              {syncResult && (
                <div style={{padding:'8px 12px',marginBottom:12,background:'rgba(22,163,74,0.08)',border:'1px solid rgba(22,163,74,0.22)',borderRadius:8,fontSize:12,color:'var(--color-success)'}}>
                  ✅ {syncResult.updated}/{syncResult.total} codes matched
                </div>
              )}

              {modalLoading ? (
                <div style={{padding:'32px',textAlign:'center',color:'var(--text-muted)'}}>Loading...</div>
              ) : !selectedCustomer ? (
                /* Tier 1: Customer list */
                <div style={{maxHeight:'60vh',overflowY:'auto'}}>
                  {customerList.length===0?(
                    <div style={{padding:'32px',textAlign:'center',color:'var(--text-muted)'}}>No pending codes</div>
                  ):customerList.map(cust=>(
                    <div key={cust.customer_id||'none'} onClick={()=>openCustomer(cust)}
                      style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 14px',marginBottom:8,
                        background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.06)',borderRadius:8,cursor:'pointer'}}
                      onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(37,99,235,0.25)'}
                      onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
                      <div>
                        <div style={{fontVariantNumeric:'tabular-nums',fontWeight:600,fontSize:13,fontWeight:700,color:cust.customer_id?'var(--accent)':'var(--text-muted)',marginBottom:3}}>{cust.customer_code}</div>
                        <div style={{fontSize:12,color:'var(--text-secondary)'}}>{cust.customer_name}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--color-info)',background:'rgba(37,99,235,0.10)',border:'1px solid rgba(37,99,235,0.20)',padding:'3px 8px',borderRadius:20}}>{cust.expected.length} codes</span>
                        <button onClick={e=>{ e.stopPropagation(); handleDeleteCustomerExpected(cust); }}
                          title={`Delete all ${cust.expected.length} pending codes for this customer`}
                          style={{background:'transparent',border:'1px solid rgba(220,38,38,0.22)',borderRadius:5,padding:'3px 8px',fontSize:12,color:'#b91c1c',cursor:'pointer',lineHeight:1}}>
                          ×
                        </button>
                        <span style={{color:'var(--text-muted)'}}>→</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Tier 2: Expected codes → click "🔗 Assign" to open the search modal */
                <div style={{maxHeight:'60vh',overflowY:'auto'}}>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:10}}>
                    Click "🔗 Assign" on a code to find the matching scanned shipment
                  </div>
                  {customerExpected.map(exp =>
                    <ExpectedRow key={exp.id + '-' + reloadCount} exp={exp} onOpenMatch={openMatch} onDelete={handleDeleteExpected} />
                  )}
                </div>
              )}
            </>
            )}
          </div>
        </div>
      )}

      {/* Image label modal (overlay on top of match modal) */}
      {imageModal && (
        <div onClick={()=>setImageModal(null)}
          style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200,padding:20}}>
          <img src={imageModal} alt="label" onClick={e=>e.stopPropagation()}
            style={{maxWidth:'100%',maxHeight:'90vh',borderRadius:8,border:'1px solid rgba(15,23,42,0.10)'}} />
          <button onClick={()=>setImageModal(null)}
            style={{position:'absolute',top:20,right:20,background:'rgba(15,23,42,0.25)',border:'1px solid rgba(15,23,42,0.14)',color:'#fff',borderRadius:'50%',width:40,height:40,fontSize:20,cursor:'pointer'}}>×</button>
        </div>
      )}

      {/* Inside images modal */}
      {insideModal && (
        <InsideImagesModal shipmentId={insideModal.id} tracking={insideModal.tracking} onClose={()=>setInsideModal(null)} />
      )}
    </div>
  );
}

const s = {
  topbar:{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:32,gap:16,flexWrap:'wrap'},
  alertGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:24},
  alertCard:{background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:'var(--radius-xl)',padding:'20px 22px',transition:'border-color var(--transition-base), box-shadow var(--transition-base)'},
  alertLabel:{fontSize:10,fontWeight:600,color:'var(--text-muted)',letterSpacing:'0.16em',textTransform:'uppercase',marginBottom:10},
  alertNum:{fontVariantNumeric:'tabular-nums',fontSize:36,fontWeight:700,lineHeight:1,marginBottom:8,letterSpacing:'-0.02em'},
  alertSub:{fontSize:12,color:'var(--text-muted)'},
  grid:{display:'grid',gridTemplateColumns:'repeat(5, minmax(0, 1fr))',gap:12},
  khoCard:{background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:'var(--radius-xl)',padding:'18px 20px',transition:'border-color var(--transition-base), box-shadow var(--transition-base)'},
  khoName:{fontSize:13,fontWeight:600,color:'var(--text-primary)',marginBottom:2},
  khoInternal:{fontSize:10,color:'var(--text-muted)',marginBottom:12,letterSpacing:'0.08em'},
  khoNum:{fontVariantNumeric:'tabular-nums',fontSize:34,fontWeight:700,lineHeight:1,marginBottom:6,letterSpacing:'-0.02em'},
  khoLabel:{fontSize:11,color:'var(--text-muted)',fontWeight:500},
  empty:{padding:'40px',textAlign:'center',color:'var(--text-muted)',fontSize:13,background:'var(--bg-secondary)',borderRadius:'var(--radius-xl)',border:'1px solid var(--border)'},
  overlay:{position:'fixed',inset:0,background:'rgba(15,23,42,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100,padding:20},
  modalBox:{background:'var(--bg-secondary)',border:'1px solid var(--border-strong)',borderRadius:'var(--radius-xl)',padding:24,width:540,maxWidth:'100%',boxShadow:'var(--shadow-md)'},
};
